import { useRef, useState } from 'react'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { InlineAlert } from '../../../components/InlineAlert'
import { ApiError } from '../../../api/problem'
import { cloneProgram } from '../../../api/cartridgeStudio'
import type { ClonedProgramView, ProgramView } from '../../../types/cartridgeStudio'

const LEVELS = ['national', 'federal', 'state', 'provincial']

export interface CloneProgramDialogProps {
  sourceProgram: ProgramView
  onClose: () => void
  onCloned: (cloned: ClonedProgramView) => void
}

/**
 * `POST /api/v1/programs/{sourceProgramId}/clones` (IB-285/IB-211 S2, ADR-0055, FR-M7.4) —
 * template-clone a source program (state-to-state / country-baseline) into a NEW draft program.
 * Deliberately mirrors `NewProgramDialog`'s shape (same fields, same error-surfacing pattern) —
 * a clone IS a create, just seeded from a resolved source instead of a blank form.
 *
 * The result is never published by cloning: the cloned RuleSet + every cloned Parameter land as
 * `status=draft` and must independently travel the full maker-checker lifecycle (submit → approve
 * by a DISTINCT checker → publish, ADR-0054) before they resolve as canon — called out explicitly
 * below so the affordance never reads as "instant new cartridge."
 */
export function CloneProgramDialog({ sourceProgram, onClose, onCloned }: CloneProgramDialogProps) {
  const [targetJurisdiction, setTargetJurisdiction] = useState(sourceProgram.jurisdiction)
  const [targetLevel, setTargetLevel] = useState(sourceProgram.level)
  const [targetCode, setTargetCode] = useState('')
  const [asOf, setAsOf] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const jurisdictionRef = useRef<HTMLInputElement>(null)

  const isValid = targetJurisdiction.trim() !== '' && targetCode.trim() !== ''

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const cloned = await cloneProgram(sourceProgram.id, {
        targetJurisdiction: targetJurisdiction.trim(),
        targetCode: targetCode.trim(),
        targetLevel,
        asOf: asOf || null,
      })
      onCloned(cloned)
    } catch (err) {
      setError(friendlyCloneError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="clone-program-title"
      title={`Clone ${sourceProgram.jurisdiction} · ${sourceProgram.code}`}
      onClose={onClose}
      initialFocusRef={jurisdictionRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={!isValid || submitting}>
            {submitting ? 'Cloning…' : 'Clone program'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        <InlineAlert severity="info">
          Cloning deep-copies the source&apos;s currently-effective RuleSet and published Parameters into a
          NEW <strong>draft</strong> program. Nothing publishes automatically — the clone still has to be
          submitted for review and approved by a different reviewer before it can publish (maker ≠ checker).
        </InlineAlert>

        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="clone-program-jurisdiction">
            Target jurisdiction
          </label>
          <input
            id="clone-program-jurisdiction"
            ref={jurisdictionRef}
            className="iw-input"
            value={targetJurisdiction}
            required
            onChange={(e) => setTargetJurisdiction(e.target.value)}
            placeholder="e.g. Louisiana"
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="clone-program-level">
            Target level
          </label>
          <select
            id="clone-program-level"
            className="iw-input"
            value={targetLevel}
            onChange={(e) => setTargetLevel(e.target.value)}
          >
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="clone-program-code">
            Target program code
          </label>
          <input
            id="clone-program-code"
            className="iw-input"
            value={targetCode}
            required
            onChange={(e) => setTargetCode(e.target.value)}
            placeholder={`e.g. ${sourceProgram.code}-CLONE`}
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="clone-program-as-of">
            Source snapshot as of (optional)
          </label>
          <input
            id="clone-program-as-of"
            type="date"
            className="iw-input"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
            Defaults to today — selects which of the source&apos;s published RuleSet versions is cloned.
          </span>
        </div>
      </form>
    </FocusTrapDialog>
  )
}

/**
 * Every failure mode called out in the IB-285 DoR: 409 duplicate target identity, 404 no effective
 * source RuleSet (or source Program missing), 403 tenant caller (clone is platform-only). The
 * backend's RFC 9457 body already carries a human-readable `detail`; this only adds the "what kind
 * of problem is this" framing the raw `type` URI slug (`.../problems/<slug>`) identifies.
 */
function friendlyCloneError(err: unknown): string {
  if (err instanceof ApiError) {
    const slug = err.problem.type?.split('/').pop()
    const detail = err.problem.detail
    if (err.status === 409 || slug === 'program_unique_identity') {
      return detail
        ? `A program with this jurisdiction, level, and code already exists. ${detail}`
        : 'A program with this jurisdiction, level, and code already exists. Choose a different target.'
    }
    if (err.status === 403 || slug === 'capability-denied') {
      return detail
        ? `Cloning is a platform-only canon operation. ${detail}`
        : 'Cloning is a platform-only canon operation — your current session cannot perform it.'
    }
    if (err.status === 404 || slug === 'not-found') {
      return detail ?? 'The source program (or an effective RuleSet as of that date) could not be found.'
    }
    return detail ?? err.message
  }
  return 'Could not clone this program. Please try again.'
}
